import Segmented from '../shared/Segmented.js';
import {RuminantGroupLabel, ruminantTrend} from '../cattle/ruminantPresentation.js';
import Button from '../shared/Button.js';
// Phase 2 Round 3 extraction (verbatim).
import React from 'react';
import {openableProps} from '../shared/openable.js';
import {toISO, addDays, todayCentralISO} from '../lib/dateUtils.js';
import PlannerIcon from '../components/PlannerIcon.jsx';
import InlineNotice from '../shared/InlineNotice.jsx';
import {ANIMAL_ICON_KEYS} from '../lib/plannerIcons.js';
import {StatStrip} from '../shared/StatStrip.js';
import {groupColor} from '../shared/GroupPillPicker.js';

import {useUI} from '../contexts/UIContext.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {SheepHomeCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import Header from '../shared/Header.tsx';
import type {SheepRecord, SheepWeighInRow} from '../lib/sheepFlockFilters.js';
import type {Notice} from '../shared/InlineNotice.jsx';

// The sheep-record authority (src/lib/sheepFlockFilters.ts) covers every column
// this dashboard reads except purchase_date, which only the recently-purchased
// weight estimate below uses. Added here rather than to that module because it
// takes no part in any filter or sort it owns.
type SheepDashboardRow = SheepRecord & {purchase_date?: string | null};

// One sheep_dailys row, as this dashboard reads it. `feeds` is the jsonb array
// migration 012 populates; its elements are read by name below, so they are
// modelled to exactly that depth and no further.
type SheepDailyFeed = {
  category?: string | null;
  unit?: string | null;
  qty?: number | string | null;
  feed_name?: string | null;
  lbs_as_fed?: number | string | null;
};

type SheepDailyRow = {
  date?: string | null;
  mortality_count?: number | string | null;
  fence_voltage_kv?: number | string | null;
  waterers_working?: boolean | null;
  feeds?: SheepDailyFeed[] | null;
};

type SheepHomeData = Omit<SheepHomeCoreData, 'sheep' | 'dailys' | 'weighIns'> & {
  sheep: SheepDashboardRow[];
  dailys: SheepDailyRow[];
  weighIns: SheepWeighInRow[];
};

const SHEEP_HOME_PARAMS = Object.freeze({});
const EMPTY_SHEEP_HOME_ROWS = Object.freeze([]) as readonly never[];
const EMPTY_BATCH_COUNTS = Object.freeze({total: 0, planned: 0});

function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as {message?: unknown}).message;
    return String(message || error);
  }
  return String(error);
}

const SheepHomeView = () => {
  const {setView} = useUI();
  const {useState} = React;
  const sheepHomeResource = useRouteResource(RESOURCE.SHEEP_HOME_CORE, SHEEP_HOME_PARAMS);
  const sheepHomeData = sheepHomeResource.data as SheepHomeData | null;
  const sheep = sheepHomeData ? sheepHomeData.sheep : EMPTY_SHEEP_HOME_ROWS;
  const dailys = sheepHomeData ? sheepHomeData.dailys : EMPTY_SHEEP_HOME_ROWS;
  const weighIns = sheepHomeData ? sheepHomeData.weighIns : EMPTY_SHEEP_HOME_ROWS;
  const batchCounts = sheepHomeData ? sheepHomeData.batchCounts : EMPTY_BATCH_COUNTS;
  const loading = sheepHomeResource.loading;
  const loadError: Notice | null = sheepHomeResource.error
    ? {
        kind: 'error',
        message: 'Could not load sheep dashboard. Please retry. (' + errorMessage(sheepHomeResource.error) + ')',
      }
    : null;
  const loadAll = sheepHomeResource.refetch;
  const [period, setPeriod] = useState(30);
  const FLOCKS = ['rams', 'ewes', 'feeders'];
  const FLOCK_LABELS: Record<string, string> = {rams: 'Rams', ewes: 'Ewes', feeders: 'Feeders'};
  const SEX_DEFAULT_WEIGHT: Record<string, number> = {ewe: 150, ram: 225, wether: 80};
  const ESTIMATE_WINDOW_DAYS = 120;
  const cutoff30 = toISO(addDays(todayCentralISO(), -30));

  function lastWeight(s: SheepDashboardRow) {
    if (!s.tag) return 0;
    const w = weighIns.find((x) => x.tag === s.tag);
    // parseFloat is fed the raw column; the cast keeps the call as written
    // rather than introducing String(). Same convention as src/lib/cattleNutrition.ts.
    return w ? parseFloat(w.weight as string) || 0 : 0;
  }
  function isRecentlyPurchased(s: SheepDashboardRow) {
    if (!s.purchase_date) return false;
    const ms = Date.now() - new Date(s.purchase_date + 'T12:00:00').getTime();
    return ms >= 0 && ms <= ESTIMATE_WINDOW_DAYS * 86400000;
  }
  function effectiveWeight(s: SheepDashboardRow) {
    const real = lastWeight(s);
    if (real > 0) return real;
    // `s.sex ?? ''` only satisfies the index read: a null sex missed every key
    // of SEX_DEFAULT_WEIGHT before and still does, falling through to 0.
    return isRecentlyPurchased(s) ? SEX_DEFAULT_WEIGHT[s.sex ?? ''] || 0 : 0;
  }
  function flockTotalWeight(f: string) {
    return sheep.filter((s) => s.flock === f).reduce((sum, s) => sum + effectiveWeight(s), 0);
  }
  function flockEstCount(f: string) {
    return sheep.filter((s) => s.flock === f && lastWeight(s) === 0 && isRecentlyPurchased(s)).length;
  }

  const totalSheep = sheep.filter((s) => FLOCKS.includes(s.flock ?? '')).length;
  const totalWeight = FLOCKS.reduce((s, f) => s + flockTotalWeight(f), 0);
  const totalEstimated = FLOCKS.reduce((s, f) => s + flockEstCount(f), 0);
  const dailys30 = dailys.filter((d) => (d.date || '') >= cutoff30);
  const totalMort30 = dailys30.reduce((s, d) => s + (parseInt(d.mortality_count as string) || 0), 0);
  const totalReports30 = dailys30.length;
  // Helpers to pull hay bales / alfalfa lbs / mineral pct out of the feeds/
  // minerals jsonb arrays populated by migration 012 + new submits.
  const sumBales = (d: SheepDailyRow) =>
    Array.isArray(d.feeds)
      ? d.feeds.reduce(
          (s: number, f: SheepDailyFeed) =>
            s + (f.category === 'hay' && f.unit === 'bale' ? parseFloat(f.qty as string) || 0 : 0),
          0,
        )
      : 0;
  // Alfalfa pellets only — hay category excluded so historical hay bales
  // (remapped by migration 013 to the ALFALFA cattle hay entry) don't
  // inflate alfalfa-lb totals via the bale unit-weight.
  const sumAlfalfa = (d: SheepDailyRow) =>
    Array.isArray(d.feeds)
      ? d.feeds.reduce((s: number, f: SheepDailyFeed) => {
          const nm = String(f.feed_name || '').toLowerCase();
          return s + (f.category === 'pellet' && nm.includes('alfalfa') ? parseFloat(f.lbs_as_fed as string) || 0 : 0);
        }, 0)
      : 0;
  function computeWindow(fromISO: string, toISO: string) {
    const windowDays = Math.max(
      1,
      Math.floor((new Date(toISO + 'T12:00:00').getTime() - new Date(fromISO + 'T12:00:00').getTime()) / 86400000) + 1,
    );
    // `?? ''` on a nullable date column: an absent date compared false against
    // both bounds before (null >= '2026-01-01' is false) and an empty string
    // does the same, so the row is excluded either way.
    const rows = dailys.filter((d) => (d.date ?? '') >= fromISO && (d.date ?? '') <= toISO);
    let bales = 0,
      alfalfa = 0,
      mort = 0,
      fenceSum = 0,
      fenceN = 0,
      watersOk = 0,
      watersN = 0;
    const reportDates = new Set<string | null | undefined>();
    for (const d of rows) {
      reportDates.add(d.date);
      bales += sumBales(d);
      alfalfa += sumAlfalfa(d);
      mort += parseInt(d.mortality_count as string) || 0;
      if (d.fence_voltage_kv != null) {
        fenceSum += parseFloat(d.fence_voltage_kv as string) || 0;
        fenceN++;
      }
      if (d.waterers_working != null) {
        if (d.waterers_working) watersOk++;
        watersN++;
      }
    }
    return {
      bales,
      alfalfa,
      mort,
      fenceAvg: fenceN > 0 ? fenceSum / fenceN : null,
      watersPct: watersN > 0 ? (watersOk / watersN) * 100 : null,
      reportDays: reportDates.size,
      days: windowDays,
    };
  }

  // Windows are farm-time (America/Chicago) calendar days, so they roll over at
  // farm midnight rather than UTC midnight.
  const todayISOstr = todayCentralISO();
  const periodFromISO = toISO(addDays(todayISOstr, -(period - 1)));
  const prevToISO = toISO(addDays(todayISOstr, -period));
  const prevFromISO = toISO(addDays(todayISOstr, -(period * 2 - 1)));
  const cur = computeWindow(periodFromISO, todayISOstr);
  const prev = computeWindow(prevFromISO, prevToISO);

  // Cards on this page take the redesign's card radius and shadow (R08).
  const cardS = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-card)',
    boxShadow: 'var(--shadow-card)',
  } as const;
  // Section titles: Title Case, bold, the handoff's 18/800 (T11).
  const sectionTitleS = {margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--ink)'} as const;

  const PeriodToggle = () => (
    <Segmented
      aria-label="Flock Rolling Window"
      style={{width: 'fit-content'}}
      value={String(period)}
      onChange={(v) => setPeriod(Number(v))}
      options={[
        {value: '30', label: '30 Days'},
        {value: '90', label: '90 Days'},
        {value: '120', label: '120 Days'},
      ]}
    />
  );

  return (
    <div
      style={{minHeight: '100vh', background: 'var(--bg-page)'}}
      data-sheep-home-loaded={loading || loadError ? 'false' : 'true'}
    >
      {/* This opt-in account-management modal owns its own user interaction;
          dashboard render data is exclusively sheepHomeResource above. */}
      <Header />
      <div
        style={{
          padding: '1.25rem',
          maxWidth: 1180,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
        }}
      >
        {/* N09: the dashboard's H1, as the cattle dashboard has. */}
        <h1 className="wcf-page-title">Sheep</h1>
        {loadError && (
          <div>
            <InlineNotice notice={loadError} />
            <Button variant="secondary" type="button" data-sheep-home-load-retry="1" onClick={loadAll}>
              Retry
            </Button>
          </div>
        )}
        {!loadError && (
          // R15: one joined strip with hairline dividers, not four loose cards
          // — the same strip the cattle dashboard carries.
          <StatStrip
            rootAttrs={{'data-sheep-home-strip': '1'}}
            tiles={[
              {label: 'Sheep on farm', value: totalSheep > 0 ? totalSheep.toLocaleString() : null},
              {
                label: 'Total live weight',
                value: totalWeight > 0 ? Math.round(totalWeight).toLocaleString() : null,
                unit: totalWeight > 0 ? 'lb' : undefined,
                // R11: one sub-line, and only where the reader acts on it — how
                // much of that weight is estimated rather than weighed.
                sub: totalEstimated > 0 ? totalEstimated + ' est.' : undefined,
              },
              {
                label: 'Mortality 30d',
                value: totalMort30 > 0 ? totalMort30.toLocaleString() : '0',
                // R03: a soft tint, not red ink.
                tone: totalMort30 > 0 ? 'danger' : undefined,
              },
              {label: 'Reports 30d', value: totalReports30 > 0 ? totalReports30.toLocaleString() : null},
            ]}
          />
        )}

        {!loadError && (
          <section>
            <h2 style={{...sectionTitleS, marginBottom: 10}}>Flock Breakdown</h2>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10}}>
              {/* Processing-batches shortcut tile — discoverable nav alongside
                  flock tiles. R11: label and number; the how-to sub-line that
                  explained the send flow is dropped. */}
              <div
                {...openableProps(() => setView('sheepbatches'))}
                style={{...cardS, padding: '14px 16px', cursor: 'pointer'}}
                className="hoverable-tile"
              >
                <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                  <span style={{fontSize: 16, fontWeight: 800, color: 'var(--ink)'}}>Processing Batches</span>
                  <span style={{fontSize: 13, fontWeight: 600, color: 'var(--label)'}}>
                    {batchCounts.total} {batchCounts.total === 1 ? 'batch' : 'batches'}
                  </span>
                </div>
                <div style={{fontSize: 13, color: 'var(--ink)'}}>
                  {batchCounts.planned > 0 ? (
                    <span>
                      <strong>{batchCounts.planned}</strong> planned
                    </span>
                  ) : (
                    <span style={{color: 'var(--label)'}}>No planned batches</span>
                  )}
                </div>
              </div>
              {FLOCKS.map((f) => {
                const flockSheep = sheep.filter((s) => s.flock === f);
                const lw = flockTotalWeight(f);
                const est = flockEstCount(f);
                // R06: the flock's own palette colour carries the card as a
                // light tint, the same 6% the cattle herd tiles and broiler
                // active cards use. An empty flock stays neutral.
                const hue = groupColor(FLOCKS.indexOf(f));
                return (
                  <div
                    key={f}
                    {...openableProps(() => setView('sheepflocks'))}
                    style={{
                      ...cardS,
                      padding: '14px 16px',
                      cursor: 'pointer',
                      background:
                        flockSheep.length > 0 ? 'color-mix(in srgb, ' + hue + ' 6%, var(--surface))' : cardS.background,
                    }}
                    className="hoverable-tile"
                  >
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                      <strong style={{fontSize: 16, fontWeight: 800}}>
                        <RuminantGroupLabel label={FLOCK_LABELS[f]} index={FLOCKS.indexOf(f)} />
                      </strong>
                      <span style={{fontSize: 13, fontWeight: 600, color: 'var(--label)'}}>
                        {flockSheep.length} {flockSheep.length === 1 ? 'sheep' : 'sheep'}
                      </span>
                    </div>
                    <div style={{fontSize: 13, color: 'var(--ink)'}}>
                      Live wt: <strong>{lw > 0 ? Math.round(lw).toLocaleString() + ' lb' : '—'}</strong>
                    </div>
                    {est > 0 && (
                      <div style={{fontSize: 12, color: 'var(--warn-ink)', marginTop: 2}}>
                        {est + ' est. (no weigh-in yet)'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {!loading && !loadError && (
          <section>
            <div style={{display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10}}>
              <h2 style={sectionTitleS}>Farm-Wide Dailys {'—'} Rolling Window</h2>
              <PeriodToggle />
            </div>
            <div style={{...cardS, overflow: 'hidden'}}>
              <div
                style={{
                  background: 'var(--surface)',
                  borderBottom: '1px solid var(--hairline)',
                  padding: '12px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: 'var(--ink)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <PlannerIcon iconKey={ANIMAL_ICON_KEYS.sheep} size={20} />
                  <span>All Flocks</span>
                </span>
                <span style={{fontSize: 12.5, color: 'var(--label)'}}>
                  {cur.reportDays} of {cur.days} days reported
                </span>
              </div>
              <div style={{padding: '14px 20px'}}>
                <div style={{fontSize: 13, color: 'var(--label)', marginBottom: 12}}>
                  Waterers Checked {cur.watersPct != null ? Math.round(cur.watersPct) + '%' : '—'}
                </div>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8}}>
                  {[
                    {
                      l: 'Bales of hay',
                      v: cur.bales > 0 ? cur.bales.toFixed(2) : '—',
                      trend: ruminantTrend(cur.bales, prev.bales),
                    },
                    {
                      l: 'Alfalfa lb',
                      v: cur.alfalfa > 0 ? Math.round(cur.alfalfa).toLocaleString() : '—',
                      trend: ruminantTrend(cur.alfalfa, prev.alfalfa),
                    },
                    {
                      l: 'Fence voltage',
                      v: cur.fenceAvg != null ? cur.fenceAvg.toFixed(1) + ' kV' : '—',
                      trend: ruminantTrend(cur.fenceAvg, prev.fenceAvg),
                      // Null hoisted to the front of the chain instead of the
                      // back. It is the same mapping: a null average failed both
                      // `>=` comparisons (null coerces to 0) and then failed the
                      // `!= null` test, so it already resolved to null here.
                      color:
                        cur.fenceAvg == null
                          ? null
                          : cur.fenceAvg >= 4
                            ? 'var(--ok-ink)'
                            : cur.fenceAvg >= 2
                              ? 'var(--warn-ink)'
                              : 'var(--danger-ink)',
                    },
                    {l: 'Mortality', v: String(cur.mort || 0), warn: cur.mort > 0},
                  ].map((it) => (
                    // A well inside the card: the control radius, not the card's
                    // (R08), and no border of its own.
                    <div
                      key={it.l}
                      style={{
                        padding: '10px 12px',
                        background: 'var(--surface-2)',
                        borderRadius: 'var(--r-control)',
                        minWidth: 0,
                        overflow: 'hidden',
                      }}
                    >
                      {/* An eyebrow above a number (section 3 Type): 11px / 700 /
                          uppercase, in the label ink. It was 9px faint text,
                          below every type rule and under 4.5:1. */}
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: 'var(--label)',
                          textTransform: 'uppercase',
                          letterSpacing: '.06em',
                          marginBottom: 4,
                        }}
                      >
                        {it.l}
                      </div>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 800,
                          color: it.warn ? 'var(--danger-ink)' : it.color || 'var(--ink)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {it.v}
                        {it.trend}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {loading && (
          <div style={{textAlign: 'center', padding: '2rem', color: 'var(--ink-faint)', fontSize: 13}}>
            Loading{'…'}
          </div>
        )}
        {!loading && !loadError && totalSheep === 0 && (
          <div
            style={{
              ...cardS,
              padding: '24px',
              textAlign: 'center',
              color: 'var(--ink-muted)',
              fontSize: 13,
            }}
          >
            No sheep on file yet. Click <strong>Flocks</strong> in the sub-nav, then <strong>Bulk Import</strong> to
            load your records.
          </div>
        )}
      </div>
    </div>
  );
};

export default SheepHomeView;
